import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import type { StepperProps, StepProps } from './Stepper.types';
import Icon from '../icon/Icon';
import './stepper.scss';

const Stepper = ({ stepperList, inline, direction, color }: StepperProps) => {
  const wDirection = direction === 'vertical' ? 'sb-stepper--vertical' : 'sb-stepper--horizontal';
  const isInline = inline ? 'sb-stepper--inline' : '';

  const dimensions = direction === 'vertical' ?
    {
      width: 1.6,
      height: 2
    } :
    inline ?
      {
        width: 1.4,
        height: 2
      } :
      {
        width: 3,
        height: 1.6
      }

  return (
    <div
      className={['sb-stepper',
        `sb-stepper--${color}`,
        wDirection,
        isInline].join(' ')}
      data-testid="sb-stepper"
    >
      {stepperList &&
        stepperList.map((step, index) => <Step
          step={step}
          index={index}
          dimensions={dimensions}
          key={`sb-step-${index}`}
        />)
      }
    </div>
  );
};

const Step = ({ step, index, dimensions }: StepProps) => {
  const isActived = !!step.isActive && !step?.hasError ? 'sb-step--active' : '';
  const isCompleted = !!step.isCompleted && !step?.hasError ? 'sb-step--completed' : '';
  const isDisabled = !!step.isDisabled && !step?.hasError ? 'sb-step--disabled' : '';
  const hasError = !!step?.hasError ? 'sb-step--error' : '';
  const hasProgress = !!step.progress ? 'sb-step--progress' : '';
  const progress = !!step.progress ? `${100 - step.progress}%` : 0;
  const stepRef = useRef<HTMLDivElement>(null);

  const animationClick = (e: any) => {
    e.preventDefault()
    e.stopPropagation()

    const element = e.target.nodeName === 'DIV' && e?.target?.className?.indexOf('sb-step__container') > -1 ?
      e.target :
      e?.target?.closest('.sb-step__container')


    const { pageX, pageY } = e;
    const { width, height } = element.getBoundingClientRect();

    element.style.setProperty("--width", `${dimensions ? (width * dimensions.width) : 0}px`);
    element.style.setProperty("--height", `${dimensions ? (height * dimensions.height) : 0}px`);

    element.style.setProperty("--top", `${dimensions ? ((pageY / height / dimensions.height) * 100) : 0}%`);
    element.style.setProperty("--left", `${dimensions ? ((pageX / width / dimensions.width) * 100) : 0}%`);

    const dx = dimensions ? (pageX > (width / dimensions.width) ? pageX : width / dimensions.width - pageX) : 0;
    const dy = dimensions ? (pageY > (height / dimensions.height) ? pageY : height / dimensions.height - pageY) : 0;
    const size = Math.sqrt(dx ** 2 + dy ** 2) * 2;

    element.style.setProperty("--size", `${size}px`);
  }

  useEffect(() => {
    if (progress && stepRef.current) {
      stepRef.current.style.setProperty('--progress', progress);
    }
  }, [progress]);

  return (
    <div
      className={['sb-step',
        isActived,
        isCompleted,
        isDisabled,
        hasError,
        hasProgress].join(' ')}
      data-testid="sb-step"
      ref={stepRef}
    >
      {!(index === 0) && <div className="sb-step__line" />}
      <div
        className="sb-step__container"
        onMouseOver={animationClick}
      >
        <div className="sb-step__hover"></div>
        {
          !!step?.hasError ?
            <Icon
              iconName="alert-triangle"
              className="text-error"
              size={24}
            />
            :
            <div
              className="sb-step__circle"
              style={{ lineHeight: "3.2rem", width: "32px", height: "32px" }}
            >
              {
                !!step.isCompleted ?
                  <Icon
                    iconName="check"
                    size={15}
                  /> :
                  !!step.progress ?
                    <div className="sb-step__circle--border">
                      <div className="sb-step__circle--content">
                        {
                          !!step.iconName?.length ?
                            <Icon
                              iconName={step.iconName}
                              size={15}
                            />
                            :
                            step.order
                        }
                      </div>
                    </div> :
                    !!step.iconName?.length ?
                      <Icon
                        iconName={step.iconName}
                        size={15}
                      /> :
                      step.order
              }
            </div>
        }
        <span className="sb-step__text">
          <strong>{step.label}</strong>
          {step.optional !== undefined && step.optional}
        </span>
      </div>
    </div>
  )
}

Stepper.propTypes = {
  /**
 * List of Steps
 */
  stepperList: PropTypes.arrayOf(PropTypes.object),
  /**
* Inline step
*/
  inline: PropTypes.bool,
  /**
* Type of horizontal
*/
  direction: PropTypes.oneOf(['horizontal', 'vertical']),
  /**
* Color of Step
*/
  color: PropTypes.oneOf(['primary', 'secondary', 'tertiary']),
};

Stepper.defaultProps = {
  inline: true,
  stepperList: [
    {
      order: 1,
      label: "Label",
      isActive: true,
      isCompleted: true,
      isIncompleted: false,
      isDisabled: false,
      iconName: ""
    },
    {
      order: 2,
      label: "Label",
      isActive: true,
      isCompleted: true,
      isDisabled: false,
      iconName: ""
    },
    {
      order: 3,
      label: "Label",
      progress: 30,
      isActive: true,
      isCompleted: true,
      isDisabled: false,
      iconName: ""
    },
    {
      order: 4,
      label: "Label",
      isActive: true,
      isCompleted: false,
      isDisabled: false,
      iconName: ""
    },
    {
      order: 5,
      label: "Label",
      isActive: false,
      isCompleted: false,
      isDisabled: true,
      iconName: ""
    },
  ],
  color: "primary",
  direction: "horizontal",
};

export default Stepper;